import React, { Fragment, useMemo } from "react";
import { HiddenVisually, Stack, Text } from "@bookingcom/bui-react";
import { useI18n, I18nChildContext } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { UITimelineLeg } from "@flights/types";
import { SPACER, SPACER_PADDED } from "@flights/constants";
import useCabinClassName from "../../../../hooks/useCabinClassName";
import useSearchCabinClass from "../../../../hooks/useSearchCabinClass";
import { formattingUtils } from "@flights/web-api-utils-universal";

export default function TimelineLegInfoInline({ leg }: { leg: UITimelineLeg }) {
  const i18n = useI18n() as I18nChildContext;
  const cabinClassName = useCabinClassName();
  const { searchCabinClass } = useSearchCabinClass();

  const { carrier, carrierLogo, cabinClass, operatingCarrier, operatingCarrierDisclosureText, flightDurationMinutes } =
    leg;

  const isMismatchCabinClass = useMemo(
    () => !!searchCabinClass && searchCabinClass !== cabinClass,
    [searchCabinClass, cabinClass]
  );

  return (
    <Stack gap={2} direction="row" alignItems="start">
      <Stack.Item attributes={{ "data-testid": "timeline_leg_info_logo" }}>
        <img role="presentation" alt={carrier} src={carrierLogo} width={24} height={24} />
      </Stack.Item>
      <Stack.Item attributes={{ "data-testid": "timeline_leg_info_logo" }} grow>
        <Stack direction="row" gap={1}>
          {[
            <Text
              key="carrier"
              variant="small_1"
              tagName="span"
              attributes={{ "data-testid": "timeline_leg_info_carrier" }}
            >
              {carrier}
            </Text>,
            operatingCarrierDisclosureText ? (
              <Text variant="small_1">
                {i18n.trans(
                  t("flights_airline_operated_by", {
                    variables: {
                      airline_name: operatingCarrierDisclosureText
                    }
                  })
                )}
              </Text>
            ) : undefined,
            !operatingCarrierDisclosureText && operatingCarrier ? (
              <Text variant="small_1">
                {i18n.trans(
                  t("flights_airline_operated_by", {
                    variables: {
                      airline_name: operatingCarrier
                    }
                  })
                )}
              </Text>
            ) : undefined,
            <Text
              key="cabin"
              tagName="span"
              variant="small_1"
              attributes={{ "data-testid": "timeline_leg_info_flight_number_and_class" }}
            >
              {getLegFullFlightNumber(leg)}
              {SPACER_PADDED}
              {isMismatchCabinClass ? (
                <span style={{ backgroundColor: "var(--bui_color_callout_background_alt)" }}>
                  {cabinClassName(cabinClass)}
                </span>
              ) : (
                cabinClassName(cabinClass)
              )}

              {/*eslint-disable-next-line flights/no-unassigned-todo-comments*/}
              {/* TODO(asamilyak): support also plane type here */}
            </Text>,
            <Text
              key="duration"
              tagName="span"
              variant="small_1"
              attributes={{
                "data-testid": "timeline_leg_info_duration",
                "aria-hidden": "true"
              }}
            >
              {i18n.trans(
                t("flights_itinerary_duration", {
                  variables: {
                    duration: formattingUtils.flightDuration(flightDurationMinutes, i18n)
                  }
                })
              )}
            </Text>
          ]
            .filter(Boolean)
            .map((el, idx) => (
              <Fragment key={idx}>
                {idx !== 0 ? SPACER : undefined}
                {el}
              </Fragment>
            ))}
          <HiddenVisually>
            {i18n.trans(
              t("flights_itinerary_duration", {
                variables: {
                  duration: formattingUtils.flightDurationAria(flightDurationMinutes, i18n)
                }
              })
            )}
          </HiddenVisually>
        </Stack>
      </Stack.Item>
    </Stack>
  );
}

const getLegFullFlightNumber = (leg: UITimelineLeg): string => {
  /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
  /* TODO(asamilyak): do we have a utility function for that? */
  return `${leg.carrierCode}${leg.flightNumber}`;
};
